<template>
  <div class="payment-page">
    <van-nav-bar fixed title="收银台" left-text="返回" left-arrow @click-left="goBack" />
    <div class="payment-body">
      <!-- 订单金额 -->
      <div class="amount-card">
        <div class="amount-label">订单金额</div>
        <div class="amount-value">￥{{ payAmount }}</div>
        <div class="order-no">订单号：{{ orderNo }}</div>
      </div>

      <!-- 支付方式 -->
      <div class="method-card">
        <div class="card-title">选择支付方式</div>
        <!-- 微信支付：默认 JSAPI（微信内置浏览器免跳转拉起微信收银台） -->
        <div
          v-if="wechatEnabled"
          class="method-item"
          :class="{ active: paymentMethod === 'wechat_jsapi' }"
          @click="selectWechat"
        >
          <span class="method-icon wechat">微</span>
          <span class="method-name">微信支付</span>
          <span class="method-desc">微信内免跳转支付（JSAPI）</span>
          <van-icon name="checked" size="20" :color="paymentMethod === 'wechat_jsapi' ? '#07c160' : '#c8c9cc'" />
        </div>
        <div
          v-if="alipayEnabled"
          class="method-item"
          :class="{ active: paymentMethod === 'alipay' }"
          @click="paymentMethod = 'alipay'"
        >
          <span class="method-icon alipay">支</span>
          <span class="method-name">支付宝支付</span>
          <span class="method-desc">支付宝账户余额、花呗等</span>
          <van-icon name="checked" size="20" :color="paymentMethod === 'alipay' ? '#1677ff' : '#c8c9cc'" />
        </div>
        <!-- 余额支付 -->
        <div
          v-if="balanceEnabled"
          class="method-item"
          :class="{ active: paymentMethod === 'balance' }"
          @click="paymentMethod = 'balance'"
        >
          <span class="method-icon balance">余</span>
          <span class="method-name">余额支付</span>
          <span class="method-desc" :class="{ 'balance-insufficient': !balanceEnough }">
            当前余额 ¥{{ Number(userBalance).toFixed(2) }}{{ !balanceEnough ? '（余额不足）' : '' }}
          </span>
          <van-icon name="checked" size="20" :color="paymentMethod === 'balance' ? '#ffb400' : '#c8c9cc'" />
        </div>
        <div class="pay-none-tip" v-if="!wechatEnabled && !alipayEnabled && !balanceEnabled">当前商城暂未开通任何支付方式，请联系客服。</div>
      </div>

      <!-- 倒计时提示 -->
      <div class="countdown-tip" v-if="countdown > 0">
        请在 <b>{{ formatCountdown(countdown) }}</b> 内完成支付，超时订单将自动关闭
      </div>

      <!-- 支付按钮 -->
      <button
        class="pay-btn"
        :style="{ background: paymentMethodColor }"
        :disabled="paying || paid"
        @click="confirmPay"
      >
        <span v-if="paying" class="spinner"></span>
        {{ paying ? '支付处理中...' : `确认支付 ￥${payAmount}` }}
      </button>

      <!-- 支付状态提示 -->
      <div class="mock-tip" v-if="paying && !qrVisible">正在请求支付平台，请稍候…</div>

      <!-- 微信 Native 扫码支付弹窗 -->
      <div class="qr-mask" v-if="qrVisible" @click="closeQr">
        <div class="qr-box" @click.stop>
          <div class="qr-title">微信扫码支付</div>
          <img class="qr-img" v-if="qrCodeDataUrl" :src="qrCodeDataUrl" alt="支付二维码" />
          <div class="qr-tip">请使用微信「扫一扫」完成支付</div>
          <div class="qr-amount">￥{{ payAmount }}</div>
          <span class="qr-close" @click="closeQr">暂不支付</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { payOrderApi, getPaymentResultApi, getPaySettingsApi } from '@/api/pay'
import { getUserInfoDetail } from '@/api/user'
import { Toast } from 'vant'
import QRCode from 'qrcode'

export default {
  name: 'Payment',
  data () {
    return {
      // wechat_jsapi（微信JSAPI，默认）/ alipay / balance
      paymentMethod: 'wechat_jsapi',
      // 支付方式开关（后台系统设置控制）
      paySettings: {
        payment_wechat_enabled: 1,
        payment_alipay_enabled: 1,
        payment_balance_enabled: 1
      },
      // 当前账户可用余额（余额支付用）
      userBalance: 0,
      paying: false,
      paid: false,
      countdown: 900,
      timer: null,
      // 真实支付
      pollTimer: null,
      // 轮询时是否同时向微信主动查单（仅微信支付兜底场景开启；支付宝跳转轮询不开启）
      pollQueryWechat: false,
      qrVisible: false,
      qrCodeDataUrl: '',
      // 微信 JSAPI
      isWechat: false,
      openid: '',
      // 微信内未授权待授权标记（点击「确认支付」时再触发 OAuth）
      needOauth: false,
      oauthRedirecting: false
    }
  },
  computed: {
    orderId () { return this.$route.query.orderId },
    orderNo () { return this.$route.query.orderNo || '' },
    payAmount () { return Number(this.$route.query.amount) || 0 },
    wechatEnabled () { return this.paySettings.payment_wechat_enabled !== 0 },
    alipayEnabled () { return this.paySettings.payment_alipay_enabled !== 0 },
    balanceEnabled () { return this.paySettings.payment_balance_enabled !== 0 },
    // 余额是否足够支付当前订单
    balanceEnough () {
      return this.userBalance >= this.payAmount
    },
    paymentMethodColor () {
      if (this.paymentMethod === 'alipay') return '#1677ff'
      if (this.paymentMethod === 'balance') return '#ffb400'
      return '#07c160'
    }
  },
  created () {
    if (!this.orderId) {
      Toast('缺少订单信息')
      this.$router.replace('/home')
      return
    }
    this.loadPaySettings()
    this.loadUserBalance()
    this.initWechat()
    // 15 分钟倒计时
    this.timer = setInterval(() => {
      this.countdown--
      if (this.countdown <= 0) {
        clearInterval(this.timer)
        this.timer = null
        this.stopPolling()
        Toast('支付超时，订单已自动关闭')
        this.$router.replace('/myorder')
      }
    }, 1000)
  },
  beforeDestroy () {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
    this.stopPolling()
  },
  methods: {
    // 读取支付方式开关，并确保当前选中的方式可用
    async loadPaySettings () {
      try {
        const { data } = await getPaySettingsApi()
        if (data) {
          this.paySettings = data
          // 若当前支付方式未开启，切换到已开启的方式（优先微信 → 支付宝 → 余额）
          const currentIsWechat = this.paymentMethod === 'wechat' || this.paymentMethod === 'wechat_jsapi'
          if (currentIsWechat && !this.wechatEnabled && this.alipayEnabled) {
            this.paymentMethod = 'alipay'
          } else if (!currentIsWechat && !this.alipayEnabled && this.wechatEnabled) {
            this.paymentMethod = 'wechat_jsapi'
          } else if ((!this.wechatEnabled && !this.alipayEnabled) && this.balanceEnabled) {
            this.paymentMethod = 'balance'
          }
        }
      } catch (e) {
        // 加载失败时默认全部开启，不影响支付
        console.error('加载支付设置失败', e)
      }
    },
    // 加载当前账户可用余额
    async loadUserBalance () {
      try {
        const { data } = await getUserInfoDetail()
        this.userBalance = Number(data.balance) || 0
      } catch (e) {
        this.userBalance = 0
      }
    },
    // 检测微信内置浏览器 & 读取 openid
    initWechat () {
      this.isWechat = /MicroMessenger/i.test(navigator.userAgent)
      // openid 优先取 OAuth 回跳参数，其次取本地缓存（避免已授权用户每次进入都重复跳授权）
      this.openid = this.$route.query.openid || window.localStorage.getItem('wechat_openid') || ''
      if (this.$route.query.openid) {
        window.localStorage.setItem('wechat_openid', this.$route.query.openid)
      }
      // 微信支付默认使用 JSAPI（微信内置浏览器免跳转拉起微信收银台）
      this.paymentMethod = 'wechat_jsapi'
      // 授权失败标记（oauth_error=xxx）：给出明确错误提示，不再自动重定向，避免死循环
      const oauthErr = this.$route.query.oauth_error || ''
      if (oauthErr) {
        this.oauthRedirecting = false
        const msgs = {
          wecom: '微信支付配置错误：当前 WECHAT_APP_ID 是企业微信 CorpID（ww 开头），JSAPI 支付需使用公众号/服务号 AppID（wx 开头）。请到 mp.weixin.qq.com「设置与开发 → 基本配置」获取正确的 AppID 后更新 server/payment_config.php',
          secret: '微信支付配置错误：未配置 WECHAT_APP_SECRET，无法获取用户 openid。请到 mp.weixin.qq.com「设置与开发 → 基本配置」获取 AppSecret 后填入 server/payment_config.php',
          '1': '微信授权失败，请确认公众号已配置网页授权域名（www.3v3v.cn）后再试'
        }
        // 后端透传的微信具体错误（invalid appid / invalid appsecret / invalid code 等），直接展示便于定位
        const errDetail = this.$route.query.errmsg || ''
        const tip = msgs[oauthErr] || (errDetail ? '微信授权失败：' + errDetail : '微信授权失败，请重试')
        Toast(tip)
        return
      }
      // 微信内未授权：标记为「待授权」，由进入页面时的自动发起流程触发 OAuth 静默授权
      this.needOauth = this.isWechat && !this.openid
      if (this.needOauth) {
        this.oauthRedirecting = false
      }
      // 微信内置浏览器：进入收银台即自动发起支付（未授权自动触发静默授权，授权回跳后自动继续），
      // 无需用户再手动点「确认支付」，从而做到「点去支付直接拉起微信收银台」
      if (this.isWechat || (this.$route.query.auto_pay === '1' && this.openid)) {
        this.$nextTick(() => this.confirmPay())
      }
      // 回调丢了 orderNo/amount 时，从订单列表补一次
      if (!this.orderNo || !this.payAmount) {
        this.fetchOrderInfo()
      }
    },
    selectWechat () {
      this.paymentMethod = 'wechat_jsapi'
    },
    goBack () {
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.replace('/myorder')
      }
    },
    formatCountdown (s) {
      const m = Math.floor(s / 60)
      const r = s % 60
      return `${m}分${String(r).padStart(2, '0')}秒`
    },
    // OAuth 回调丢了 orderNo/amount 时补订单信息（订单可能不在第一页，翻页查找）
    async fetchOrderInfo () {
      try {
        for (let page = 1; page <= 5; page++) {
          const { list } = await import('@/api/order').then(m => m.getOrderListApi('all', page)).then(r => r.data)
          const found = (list || []).find(o => o.order_id === Number(this.orderId))
          if (found) {
            this.$route.query.orderNo = found.order_no
            this.$route.query.amount = found.total_price
            return
          }
          if (!list || list.length < 10) return
        }
      } catch (e) { /* 忽略 */ }
    },
    // 发起支付：根据后端返回的支付类型分发处理
    async confirmPay () {
      if (this.paying || this.paid || this.oauthRedirecting) return
      if (!this.wechatEnabled && !this.alipayEnabled && !this.balanceEnabled) {
        Toast('当前暂无可用的支付方式，请联系客服')
        return
      }
      // 余额支付：先校验余额是否足够
      if (this.paymentMethod === 'balance') {
        if (!this.balanceEnough) {
          Toast('余额不足，当前余额 ¥' + Number(this.userBalance).toFixed(2) + '，请更换支付方式')
          return
        }
      }
      this.paying = true
      try {
        // 微信 JSAPI：微信内未授权 → 点击「确认支付」时触发 OAuth 授权，授权成功回跳后自动继续支付
        if (this.paymentMethod === 'wechat_jsapi') {
          if (this.isWechat && !this.openid) {
            Toast('正在获取微信授权...')
            window.location.href = `/api/payment/oauth?orderId=${this.orderId}&auto_pay=1`
            return
          }
          if (!this.isWechat) {
            Toast('请在微信内置浏览器中完成微信支付')
          }
        }

        const options = {}
        if (this.paymentMethod === 'wechat_jsapi') {
          options.openid = this.openid
          options.tradeType = 'jsapi'
        }
        const method = this.paymentMethod === 'wechat_jsapi' ? 'wechat' : this.paymentMethod
        const { data } = await payOrderApi(this.orderId, method, options)

        // 0. 余额支付：直接支付成功
        if (data.pay_type === 'balance') {
          Toast.success('余额支付成功')
          this.loadUserBalance()
          this.finishPayment()
          return
        }

        // 1. 沙箱模式：直接支付成功
        if (data.pay_type === 'sandbox') {
          Toast.success('支付成功')
          this.finishPayment()
          return
        }

        // 2. 支付宝：跳转到支付宝收银台
        if (data.pay_type === 'alipay') {
          // 支付宝场景下轮询只读本地订单状态，不主动向微信查单
          this.pollQueryWechat = false
          this.startPolling()
          Toast('正在跳转支付宝收银台...')
          window.location.href = data.pay_url
          return
        }

        // 3. 微信 JSAPI：调用微信内置收银台
        if (data.pay_type === 'wechat_jsapi') {
          Toast('正在拉起微信支付...')
          try {
            await this.invokeWechatJsapi(data.jsapi_params)
            // JSAPI 回调 get_brand_wcpay_request:ok 即微信已确认支付成功，直接刷新订单状态并跳转订单列表
            Toast.success('支付成功')
            this.confirmPaidAndRedirect()
          } catch (err) {
            if (err.message === '已取消支付') {
              // 取消支付：启动轮询兜底（个别场景微信可能实际已扣款但回调为 cancel）
              Toast('已取消支付')
              this.pollQueryWechat = true
              this.startPolling()
            } else {
              Toast(err.message || '微信支付失败')
              // 拉起超时/失败时用户可能已实际扣款，启动轮询兜底确认，避免订单已支付但页面无反应
              this.pollQueryWechat = true
              this.startPolling()
            }
          }
          return
        }

        // 4. 微信 H5：暂未启用（默认使用 JSAPI）
        if (data.pay_type === 'wechat_h5') {
          Toast('当前环境不支持 JSAPI，暂未启用 H5 支付')
          return
        }

        // 5. 微信 Native：展示支付二维码，等待扫码
        if (data.pay_type === 'wechat_native') {
          this.qrCodeDataUrl = await QRCode.toDataURL(data.code_url, {
            width: 240,
            margin: 2
          })
          this.qrVisible = true
          this.pollQueryWechat = true
          this.startPolling()
          return
        }

        Toast('未知支付类型')
      } catch (e) {
        // e 可能是后端返回的错误文案（字符串）、Error 对象，或极端情况下为 undefined，
        // 统一提取可读信息，避免控制台只打出「支付失败 undefined」而用户无任何提示
        const msg = e && e.message ? e.message : (typeof e === 'string' ? e : '支付失败，请稍后重试')
        console.error('支付失败', e)
        Toast(msg)
      } finally {
        this.paying = false
      }
    },
    // 调用微信 JSAPI 支付（getBrandWCPayRequest）
    invokeWechatJsapi (jsapiParams) {
      return new Promise((resolve, reject) => {
        let settled = false
        let invoked = false
        let timer = null
        const cleanup = () => {
          if (timer) { clearInterval(timer); timer = null }
          document.removeEventListener('WeixinJSBridgeReady', doInvoke)
        }
        const doInvoke = () => {
          if (settled || invoked || !window.WeixinJSBridge) return
          invoked = true
          window.WeixinJSBridge.invoke('getBrandWCPayRequest', jsapiParams, (res) => {
            if (settled) return
            settled = true
            cleanup()
            const msg = (res && res.err_msg) || ''
            if (msg === 'get_brand_wcpay_request:ok') {
              resolve()
            } else if (msg === 'get_brand_wcpay_request:cancel') {
              reject(new Error('已取消支付'))
            } else {
              reject(new Error(msg || '微信支付失败'))
            }
          })
        }
        // 优先事件监听；同时轮询兜底（部分微信版本不会触发 ready 事件，但 bridge 已就绪）
        document.addEventListener('WeixinJSBridgeReady', doInvoke, false)
        timer = setInterval(() => {
          if (window.WeixinJSBridge) doInvoke()
        }, 200)
        // 10 秒超时兜底
        setTimeout(() => {
          if (settled) return
          settled = true
          cleanup()
          // 超时提示附带 appId，便于确认当前公众号与拉起支付的公众号是否一致
          const suffix = jsapiParams && jsapiParams.appId ? '（appId:' + jsapiParams.appId + '）' : ''
          reject(new Error((window.WeixinJSBridge ? '拉起微信支付超时，请重试' : '当前微信环境暂不支持拉起支付') + suffix))
        }, 10000)
        // bridge 已就绪时立即拉起
        if (window.WeixinJSBridge) doInvoke()
      })
    },
    // JSAPI 支付成功后：短暂等待微信异步通知（notify）更新订单状态，再跳转订单列表，
    // 避免跳转后订单仍显示「待支付」。最多等约 6 秒，超时也跳转（订单列表可下拉刷新）。
    async confirmPaidAndRedirect () {
      // 微信已确认支付成功：主动向微信查单 / 轮询确认订单更新为待发货，最长等待约 18 秒，
      // 降低「微信已扣款成功但本地订单仍显示待支付」（notify 延迟/丢失）的概率
      for (let i = 0; i < 12; i++) {
        try {
          // queryWechat=true：订单仍待支付时主动向微信订单查询接口确认（notify 丢失兜底）
          // 支付后若本地订单状态尚未更新，用 paid 字段兜底判断是否已支付
          const { data } = await getPaymentResultApi(this.orderId, true)
          if (data.paid || data.status === 1) break
        } catch (e) {
          // 单次查询失败忽略，继续等待
        }
        await new Promise(resolve => setTimeout(resolve, 1500))
      }
      this.finishPayment()
    },
    // 轮询支付结果（真实支付后，每 3 秒查询一次）
    // 当 pollQueryWechat=true（微信 Native 扫码 / JSAPI 取消或拉起失败兜底）时，
    // 请求会携带 queryWechat=1，微信侧若已扣款但 notify 未到达也能主动查单纠正订单状态
    startPolling () {
      this.stopPolling()
      this.pollTimer = setInterval(async () => {
        try {
          const { data } = await getPaymentResultApi(this.orderId, this.pollQueryWechat)
          if (data.paid || data.status === 1) {
            this.stopPolling()
            this.paid = true
            this.qrVisible = false
            Toast.success('支付成功')
            setTimeout(() => {
              this.redirectAfterPay()
            }, 800)
          }
        } catch (e) {
          // 单次查询失败不中断轮询
          console.error('查询支付结果失败', e)
        }
      }, 3000)
    },
    stopPolling () {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    // 关闭二维码弹窗（订单仍待支付，可稍后再扫）
    closeQr () {
      this.qrVisible = false
    },
    // 支付完成：清理计时器并跳转「全部订单」
    finishPayment () {
      this.paid = true
      clearInterval(this.timer)
      this.timer = null
      this.stopPolling()
      setTimeout(() => {
        this.redirectAfterPay()
      }, 800)
    },
    // 支付成功后跳转「全部订单」
    // 说明：支付平台异步通知可能稍有延迟，落到「待发货」会短暂显示空列表；
    //       跳「全部订单」可立即看到刚支付的订单。
    redirectAfterPay () {
      this.$router.replace('/myorder')
    }
  }
}
</script>

<style lang="less" scoped>
.payment-page {
  min-height: 100vh;
  background: #f5f6f8;
  padding-top: 46px;
  /deep/ .van-nav-bar__arrow { color: #333; }
  /deep/ .van-nav-bar__text { color: #333; }
  .payment-body { padding: 16px; }

  .amount-card {
    background: #fff;
    border-radius: 12px;
    padding: 24px 20px;
    text-align: center;
    margin-bottom: 16px;
    .amount-label { font-size: 14px; color: #999; margin-bottom: 8px; }
    .amount-value { font-size: 40px; font-weight: 700; color: #ff6b35; margin-bottom: 8px; }
    .order-no { font-size: 12px; color: #aaa; }
  }

  .method-card {
    background: #fff;
    border-radius: 12px;
    padding: 16px 20px;
    margin-bottom: 16px;
    .card-title { font-size: 15px; font-weight: 600; color: #333; margin-bottom: 12px; }
    .method-item {
      display: flex;
      align-items: center;
      padding: 14px 0;
      border-bottom: 1px solid #f2f3f5;
      cursor: pointer;
      transition: all 0.2s;
      &:last-child { border-bottom: none; }
      &.active { background: rgba(0, 0, 0, 0.02); border-radius: 8px; padding-left: 8px; padding-right: 8px; }
      .method-icon {
        display: inline-flex; align-items: center; justify-content: center;
        width: 32px; height: 32px; border-radius: 8px; color: #fff; font-size: 16px; font-weight: 700;
        margin-right: 12px;
        &.wechat { background: #07c160; }
        &.alipay { background: #1677ff; }
        &.balance { background: #ffb400; }
      }
      .method-name { font-size: 15px; color: #333; font-weight: 500; }
      .method-desc { flex: 1; font-size: 12px; color: #999; margin-left: 12px; &.balance-insufficient { color: #e74c3c; } }
      .pay-none-tip { font-size: 13px; color: #999; padding: 12px 0 6px; text-align: center; }
    }
  }

  .countdown-tip {
    text-align: center;
    font-size: 13px;
    color: #999;
    margin-bottom: 16px;
    b { color: #ff6b35; }
  }

  .pay-btn {
    width: 100%;
    height: 48px;
    border: none;
    border-radius: 24px;
    color: #fff;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    &:active { transform: scale(0.98); }
    &:disabled { opacity: 0.6; }
    .spinner {
      width: 16px; height: 16px;
      border: 2px solid rgba(255,255,255,0.4);
      border-top-color: #fff;
      border-radius: 50%;
      margin-right: 8px;
      animation: spin 0.8s linear infinite;
    }
  }

  .mock-tip { text-align: center; font-size: 12px; color: #aaa; margin-top: 16px; }

  // 微信 Native 扫码支付弹窗
  .qr-mask {
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0, 0, 0, 0.55);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;

    .qr-box {
      width: 300px;
      background: #fff;
      border-radius: 16px;
      padding: 24px 20px;
      text-align: center;

      .qr-title { font-size: 17px; font-weight: 700; color: #1a1a2e; margin-bottom: 18px; }
      .qr-img {
        width: 240px;
        height: 240px;
        border-radius: 8px;
        background: #f7f8fa;
        border: 1px solid #f0f0f0;
      }
      .qr-tip { font-size: 13px; color: #666; margin-top: 14px; }
      .qr-amount { font-size: 24px; font-weight: 700; color: #ff6b35; margin: 6px 0 18px; }
      .qr-close {
        display: inline-block;
        padding: 8px 30px;
        border-radius: 20px;
        font-size: 13px;
        color: #999;
        background: #f2f3f5;
        cursor: pointer;
      }
    }
  }
}

@keyframes spin { to { transform: rotate(360deg); } }
</style>
